import voca from 'voca';
import { getLogger } from '@cld/upload-widget';
import getFileReader from 'utils/fileReader';

const logger = getLogger();

const ALGORITHM = 'AES-GCM';
const KEY_USAGES = ['encrypt', 'decrypt'];
const KEY_FORMAT = 'raw';

const subtleCrypto = self.crypto && self.crypto.subtle;

const importSecretKey = (rawKey) => subtleCrypto.importKey(KEY_FORMAT, rawKey, ALGORITHM, true, KEY_USAGES);

const readFile = (file, reader) => reader.readAsBuffer(file).then(({ result }) => result);

/**
 * encrypt a file for end-to-end encrypted upload - user/customer responsible for key and decryption
 * doesnt work in IE 11!
 * @param file - the file to encrypt
 * @param publicKey - encryption key
 * @param iv - initialization vector
 * @param fileReader - the file reader
 * @returns {Promise<File>}
 */
const encryptFile = async (file, publicKey, iv, fileReader) => {
  let fileContent;

  try {
    fileContent = await readFile(file, fileReader);
  } catch (err) {
    logger.error('encryption: failed to read file to encrypt', err.message);
    throw err;
  }

  const encoder = new TextEncoder();

  if (voca.isString(publicKey)) {
    publicKey = await importSecretKey(encoder.encode(publicKey));
  }

  if (voca.isString(iv)) {
    iv = encoder.encode(iv);
  }

  const encFile = await subtleCrypto.encrypt({ name: ALGORITHM, iv }, publicKey, fileContent);
  return new File([encFile], file.name, { lastModified: file.lastModified });
};

const isSupported = !!(subtleCrypto && self.TextEncoder);

const getFileEncryptor = () => {
  const reader = getFileReader(); // share a reader so reading is serialized

  return (file, publicKey, iv) => encryptFile(file, publicKey, iv, reader);
};

export { isSupported, ALGORITHM, getFileEncryptor };
